import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Paper, Chip, Alert,
  CircularProgress, Divider, Card, CardContent, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { useAuth } from '../../context/AuthContext';
import {
  useCupon,
  useReimprimirCupon,
  useRecalcularInteresesCupon,
} from '../../hooks/useCupones';
import { useCvuDeEmpresa } from '../../hooks/useCvus';
import { useDatosBancarios } from '../../hooks/useDatosBancarios';
import DatosBancariosCard from '../../components/DatosBancariosCard';
import { usePagosPorCupon } from '../../hooks/usePagos';
import { formatCurrency, formatDate } from '../../utils/format';
import { mapSupabaseError } from '../../utils/errores';
import { generarCuponPdf } from '../../utils/reportes/cuponPdf';
import { getEstadoCuponLabel, getEstadoCuponColor } from '../../types/cupon';
import {
  getEstadoPagoLabel,
  getEstadoPagoColor,
  getViaPagoLabel,
  type Pago,
} from '../../types/pago';
import RegistrarPagoDialog from '../pagos/RegistrarPagoDialog';
import InformarPagoDialog from '../pagos/InformarPagoDialog';
import AccionPagoDialog from '../pagos/AccionPagoDialog';
import ConsultarPagosDialog from '../transacciones/ConsultarPagosDialog';
import { formatCvu } from '../../types/cvu';

// ── Component ─────────────────────────────────────────────────────────────────

export default function CuponDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const puedeOperar =
    effectiveRole === 'admin_general' || effectiveRole === 'operador';

  const { data: cupon, isLoading, isError } = useCupon(id);
  const { data: pagos = [] } = usePagosPorCupon(id);
  const { data: cvuEmpresa } = useCvuDeEmpresa(cupon?.empresa_id);
  const { data: datosBancarios } = useDatosBancarios();
  const reimprimir = useReimprimirCupon();
  const recalcular = useRecalcularInteresesCupon();

  // Recálculo on-demand al abrir el detalle (decisión #029).
  // Si el cupón ya está pagado/anulado, la RPC no hace nada.
  // Si hay cambios (estado o interés), invalida las queries y se ve actualizado.
  useEffect(() => {
    if (id) {
      void recalcular.mutateAsync(id).catch(() => {
        // silencioso: no es crítico que falle al abrir el detalle
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [openRegistrar, setOpenRegistrar] = useState(false);
  const [openInformar, setOpenInformar] = useState(false);
  const [pagoSeleccionado, setPagoSeleccionado] = useState<Pago | null>(null);
  const [openConsultarPP, setOpenConsultarPP] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const copiar = (texto: string, label: string) => {
    void navigator.clipboard.writeText(texto).then(() => {
      setSnackbar(`${label} copiado al portapapeles`);
      window.setTimeout(() => setSnackbar(null), 2500);
    });
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleDescargarPdf = async () => {
    if (!id || !cupon) return;
    // Registra la reimpresión primero (incrementa contador en BD). Si falla,
    // no descargamos para no perder trazabilidad — el usuario verá el error.
    await reimprimir.mutateAsync(id);
    // Genera el PDF cliente y dispara descarga.
    // Pasa los datos del CVU si la empresa los tiene (Fase 6.1).
    generarCuponPdf(
      cupon,
      cvuEmpresa
        ? { alias: cvuEmpresa.alias, cvu: cvuEmpresa.cvu }
        : null,
      datosBancarios,
    );
  };

  // ── Loading / Error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !cupon) {
    return (
      <Alert severity="error">
        No se pudo cargar el cupón.
      </Alert>
    );
  }

  const esPagado  = cupon.estado === 'pagado';
  const esVencido = cupon.estado === 'vencido';
  const esAnulado = cupon.estado === 'anulado';
  const puedeRegistrarPago = puedeOperar && !esPagado && !esAnulado;
  // La empresa informa su transferencia y sube el comprobante (ADR #043).
  const puedeInformarPago = !puedeOperar && !esPagado && !esAnulado;

  const totalAcreditado = pagos
    .filter((p) => p.estado === 'acreditado')
    .reduce((sum, p) => sum + Number(p.monto_pagado), 0);
  const saldoPendiente = Math.max(cupon.monto_total - totalAcreditado, 0);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Navegación */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/cupones')}
        sx={{ mb: 2 }}
      >
        Volver a Cupones
      </Button>

      {/* Encabezado */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <ReceiptLongIcon color="primary" />
              <Typography variant="h6" fontWeight={700} fontFamily="monospace">
                {cupon.codigo}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {cupon.empresa?.razon_social ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Período: <strong>{cupon.periodo?.periodo ?? '—'}</strong>
              {cupon.planilla?.numero && cupon.planilla.numero > 1
                ? ` · DDJJ adicional #${cupon.planilla.numero}`
                : ''}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={getEstadoCuponLabel(cupon.estado)}
              color={getEstadoCuponColor(cupon.estado)}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Detalle financiero */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Detalle del cupón
          </Typography>

          <Stack spacing={1.5} divider={<Divider flexItem />}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Monto de aportes</Typography>
              <Typography variant="body2">{formatCurrency(cupon.monto_aporte)}</Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Intereses por mora</Typography>
              <Typography
                variant="body2"
                color={cupon.interes > 0 ? 'error' : 'inherit'}
              >
                {formatCurrency(cupon.interes)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" fontWeight={600}>Total a pagar</Typography>
              <Typography variant="h6" fontWeight={700} color="primary">
                {formatCurrency(cupon.monto_total)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Fechas */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Fecha de emisión
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(cupon.fecha_emision)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Fecha de vencimiento
            </Typography>
            <Typography
              variant="body1"
              fontWeight={500}
              color={esVencido ? 'error' : 'inherit'}
            >
              {formatDate(cupon.fecha_vencimiento)}
            </Typography>
          </Box>

          {esPagado && cupon.fecha_pago && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fecha de pago
              </Typography>
              <Typography variant="body1" fontWeight={500} color="success.main">
                {formatDate(cupon.fecha_pago)}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Cuenta bancaria única BNA (ADR #042) — solo para el rol empresa */}
      {!puedeOperar && datosBancarios && (
        <DatosBancariosCard datos={datosBancarios} codigoCupon={cupon.codigo} />
      )}

      {/* Datos de pago por CVU (Fase 6) — visible si la empresa tiene CVU */}
      {cvuEmpresa && (
        <Card sx={{ mb: 3, borderLeft: 4, borderLeftColor: 'primary.main' }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalanceIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Datos de pago por CVU
                </Typography>
              </Stack>
              {puedeOperar && (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => setOpenConsultarPP(true)}
                >
                  Verificar pagos de hoy en PP
                </Button>
              )}
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  Alias:
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                  {cvuEmpresa.alias}
                </Typography>
                <Tooltip title="Copiar alias">
                  <IconButton size="small" onClick={() => copiar(cvuEmpresa.alias, 'Alias')}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  CVU:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {formatCvu(cvuEmpresa.cvu)}
                </Typography>
                <Tooltip title="Copiar CVU">
                  <IconButton size="small" onClick={() => copiar(cvuEmpresa.cvu, 'CVU')}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  Titular:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ATSA Río Negro
                </Typography>
              </Stack>
            </Stack>

            <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
              Si tu billetera muestra <strong>"ATSARN"</strong> en el campo
              destinatario, es la abreviación de{' '}
              <strong>ATSA RIO NEGRO</strong> (Asociación de Trabajadores de la
              Sanidad Argentina — seccional Río Negro). Es el destinatario
              correcto.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Pagos (Fase 5) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <PaymentsIcon color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Pagos registrados
              </Typography>
              <Chip label={pagos.length} size="small" />
            </Stack>
            {puedeRegistrarPago && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PaymentsIcon />}
                onClick={() => setOpenRegistrar(true)}
              >
                Registrar pago
              </Button>
            )}
            {puedeInformarPago && (
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => setOpenInformar(true)}
              >
                Informar pago / Subir comprobante
              </Button>
            )}
          </Stack>

          {pagos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Sin pagos registrados para este cupón.
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Vía</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Ver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagos.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{formatDate(p.fecha_pago)}</TableCell>
                      <TableCell>{getViaPagoLabel(p.via_pago)}</TableCell>
                      <TableCell align="right">{formatCurrency(p.monto_pagado)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getEstadoPagoLabel(p.estado)}
                          color={getEstadoPagoColor(p.estado)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => setPagoSeleccionado(p)}
                          aria-label="Ver pago"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Acreditado
                </Typography>
                <Typography variant="body2" fontWeight={500} color="success.main">
                  {formatCurrency(totalAcreditado)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Saldo pendiente
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={saldoPendiente === 0 ? 'success.main' : 'warning.main'}
                >
                  {formatCurrency(saldoPendiente)}
                </Typography>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Alertas de estado */}
      {esVencido && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Este cupón está <strong>vencido</strong>. Se aplican intereses por mora.
        </Alert>
      )}
      {esPagado && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Este cupón fue <strong>pagado</strong> el {formatDate(cupon.fecha_pago)}.
        </Alert>
      )}

      {/* Acciones */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title={`Genera PDF descargable · impresión #${cupon.reimpresion + 1}`}>
          <span>
            <Button
              variant="contained"
              startIcon={reimprimir.isPending ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
              onClick={handleDescargarPdf}
              disabled={reimprimir.isPending}
            >
              {cupon.reimpresion === 0 ? 'Descargar PDF' : `Descargar PDF (${cupon.reimpresion})`}
            </Button>
          </span>
        </Tooltip>

        {cupon.reimpresion > 0 && (
          <Typography variant="caption" color="text.secondary">
            Reimpreso {cupon.reimpresion} {cupon.reimpresion === 1 ? 'vez' : 'veces'}
          </Typography>
        )}
      </Stack>

      {/* Error de reimpresión */}
      {reimprimir.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {mapSupabaseError(reimprimir.error, 'No se pudo generar el PDF del cupón.')}
        </Alert>
      )}

      {/* Dialogs Fase 5 — Pagos */}
      {openRegistrar && (
        <RegistrarPagoDialog
          open
          cupon={cupon}
          onClose={() => setOpenRegistrar(false)}
        />
      )}
      {openInformar && (
        <InformarPagoDialog
          open
          cupon={cupon}
          onClose={() => setOpenInformar(false)}
          onSuccess={() => setSnackbar('Pago informado: ATSA lo verificará y confirmará')}
        />
      )}
      {pagoSeleccionado && (
        <AccionPagoDialog
          open
          pago={pagoSeleccionado}
          modo="ver"
          onClose={() => setPagoSeleccionado(null)}
        />
      )}

      {openConsultarPP && cvuEmpresa && (
        <ConsultarPagosDialog
          open
          empresaId={cvuEmpresa.empresa_id}
          empresaLabel={cupon.empresa?.razon_social}
          onClose={() => setOpenConsultarPP(false)}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={(_e, reason) => {
          if (reason !== 'clickaway') setSnackbar(null);
        }}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
